export interface BulkMessageVariable {
  name: string;
  placeholder: string;
  description: string;
  example: string;
  category: 'client' | 'gym' | 'membership' | 'activity' | 'datetime';
  required: boolean;
  formatter?: 'text' | 'currency' | 'date' | 'datetime' | 'number';
}

export const BULK_MESSAGE_VARIABLES: BulkMessageVariable[] = [
  // Variables de Cliente
  {
    name: 'clientName',
    placeholder: '{{clientName}}',
    description: 'Nombre completo del cliente',
    example: 'Juan Pérez',
    category: 'client',
    required: false,
    formatter: 'text',
  },
  {
    name: 'clientEmail',
    placeholder: '{{clientEmail}}',
    description: 'Email del cliente',
    example: 'juan@example.com',
    category: 'client',
    required: false,
    formatter: 'text',
  },
  {
    name: 'clientPhone',
    placeholder: '{{clientPhone}}',
    description: 'Teléfono del cliente',
    example: '+51 999 999 999',
    category: 'client',
    required: false,
    formatter: 'text',
  },

  // Variables de Gimnasio
  {
    name: 'gymName',
    placeholder: '{{gymName}}',
    description: 'Nombre del gimnasio',
    example: 'Gym Fitness',
    category: 'gym',
    required: false,
    formatter: 'text',
  },
  {
    name: 'gymPhone',
    placeholder: '{{gymPhone}}',
    description: 'Teléfono del gimnasio',
    example: '+51 999 888 777',
    category: 'gym',
    required: false,
    formatter: 'text',
  },
  {
    name: 'gymAddress',
    placeholder: '{{gymAddress}}',
    description: 'Dirección del gimnasio',
    example: 'Av. Principal 123',
    category: 'gym',
    required: false,
    formatter: 'text',
  },

  // Variables de Membresía
  {
    name: 'planName',
    placeholder: '{{planName}}',
    description: 'Nombre del plan de membresía',
    example: 'Plan Premium',
    category: 'membership',
    required: false,
    formatter: 'text',
  },
  {
    name: 'membershipStatus',
    placeholder: '{{membershipStatus}}',
    description: 'Estado de la membresía',
    example: 'Activa',
    category: 'membership',
    required: false,
    formatter: 'text',
  },
  {
    name: 'daysUntilExpiration',
    placeholder: '{{daysUntilExpiration}}',
    description: 'Días restantes hasta vencimiento',
    example: '15',
    category: 'membership',
    required: false,
    formatter: 'number',
  },
  {
    name: 'expirationDate',
    placeholder: '{{expirationDate}}',
    description: 'Fecha de vencimiento',
    example: '31/12/2024',
    category: 'membership',
    required: false,
    formatter: 'date',
  },
  {
    name: 'amount',
    placeholder: '{{amount}}',
    description: 'Monto con formato de moneda',
    example: 'S/ 150.00',
    category: 'membership',
    required: false,
    formatter: 'currency',
  },

  // Variables de Fecha/Hora
  {
    name: 'date',
    placeholder: '{{date}}',
    description: 'Fecha actual',
    example: '15/10/2024',
    category: 'datetime',
    required: false,
    formatter: 'date',
  },
  {
    name: 'time',
    placeholder: '{{time}}',
    description: 'Hora actual',
    example: '14:30',
    category: 'datetime',
    required: false,
    formatter: 'text',
  },
  {
    name: 'dayOfWeek',
    placeholder: '{{dayOfWeek}}',
    description: 'Día de la semana',
    example: 'Lunes',
    category: 'datetime',
    required: false,
    formatter: 'text',
  },
];

// Variables de Actividades
export const ACTIVITY_MESSAGE_VARIABLES: BulkMessageVariable[] = [
  {
    name: 'activityName',
    placeholder: '{{activityName}}',
    description: 'Nombre de la actividad',
    example: 'Yoga Matutino',
    category: 'activity',
    required: false,
    formatter: 'text',
  },
  {
    name: 'activityDescription',
    placeholder: '{{activityDescription}}',
    description: 'Descripción de la actividad',
    example: 'Sesión de yoga para principiantes',
    category: 'activity',
    required: false,
    formatter: 'text',
  },
  {
    name: 'activityStartDate',
    placeholder: '{{activityStartDate}}',
    description: 'Fecha de inicio de la actividad',
    example: '15/10/2024',
    category: 'activity',
    required: false,
    formatter: 'date',
  },
  {
    name: 'activityStartTime',
    placeholder: '{{activityStartTime}}',
    description: 'Hora de inicio de la actividad',
    example: '08:00',
    category: 'activity',
    required: false,
    formatter: 'text',
  },
  {
    name: 'activityStartDateTime',
    placeholder: '{{activityStartDateTime}}',
    description: 'Fecha y hora de inicio de la actividad',
    example: '15/10/2024 08:00',
    category: 'activity',
    required: false,
    formatter: 'datetime',
  },
  {
    name: 'activityDuration',
    placeholder: '{{activityDuration}}',
    description: 'Duración de la actividad en minutos',
    example: '60',
    category: 'activity',
    required: false,
    formatter: 'number',
  },
  {
    name: 'activityMaxParticipants',
    placeholder: '{{activityMaxParticipants}}',
    description: 'Número máximo de participantes',
    example: '20',
    category: 'activity',
    required: false,
    formatter: 'number',
  },
  {
    name: 'activityLocation',
    placeholder: '{{activityLocation}}',
    description: 'Ubicación de la actividad',
    example: 'Av. Principal 123',
    category: 'activity',
    required: false,
    formatter: 'text',
  },
  {
    name: 'timeUntilActivity',
    placeholder: '{{timeUntilActivity}}',
    description: 'Tiempo restante hasta la actividad',
    example: '2 horas',
    category: 'activity',
    required: false,
    formatter: 'text',
  },
];

// Tipo de contexto de variables
export type VariableContextType = 'bulk' | 'activity' | 'event' | 'reminder';

// Mapeo de contextos a variables disponibles
export const VARIABLE_CONTEXT_MAP: Record<VariableContextType, BulkMessageVariable[]> = {
  bulk: BULK_MESSAGE_VARIABLES,
  activity: [...BULK_MESSAGE_VARIABLES, ...ACTIVITY_MESSAGE_VARIABLES],
  event: BULK_MESSAGE_VARIABLES, // Para futuros eventos
  reminder: BULK_MESSAGE_VARIABLES, // Para futuros recordatorios
};

// Helper para obtener variables por contexto
export function getVariablesByContext(context: VariableContextType): BulkMessageVariable[] {
  return VARIABLE_CONTEXT_MAP[context] || BULK_MESSAGE_VARIABLES;
}

// Helper para validar variables en un contexto
// Soporta tanto {{variable}} como {variable}
export function validateVariablesInContext(
  message: string,
  context: VariableContextType,
): { valid: boolean; invalidVariables: string[] } {
  // Soportar tanto {{variable}} como {variable}
  const doubleBracePattern = /\{\{(\w+)\}\}/g;
  const singleBracePattern = /\{(\w+)\}/g;

  const doubleBraceMatches = message.matchAll(doubleBracePattern);
  const singleBraceMatches = message.matchAll(singleBracePattern);

  const invalidVariables: string[] = [];
  const validVariables = getVariablesByContext(context).map((v) => v.name);

  // Validar variables con doble llave
  for (const match of doubleBraceMatches) {
    const variableName = match[1];
    if (!validVariables.includes(variableName)) {
      invalidVariables.push(variableName);
    }
  }

  // Validar variables con una sola llave
  for (const match of singleBraceMatches) {
    const variableName = match[1];
    if (!validVariables.includes(variableName) && !invalidVariables.includes(variableName)) {
      invalidVariables.push(variableName);
    }
  }

  return {
    valid: invalidVariables.length === 0,
    invalidVariables,
  };
}

export const BULK_MESSAGE_VARIABLE_CATEGORIES = {
  client: 'Cliente',
  gym: 'Gimnasio',
  membership: 'Membresía',
  activity: 'Actividad',
  datetime: 'Fecha/Hora',
} as const;
